// Provider-agnostic types. Each concrete provider (OpenAI, Anthropic,
// Gemini) translates these to its own wire format.

export type LlmProviderId = "openai" | "anthropic" | "gemini";

export interface LlmSettings {
  provider: LlmProviderId;
  /** API key. Stored in localStorage — demo only; production should
   *  proxy through an Azure backend with managed identity. */
  apiKey: string;
  /** Model identifier as the provider expects. Examples:
   *    OpenAI    → "gpt-4o-mini", "gpt-4o", "gpt-4.1"
   *    Anthropic → "claude-sonnet-4-5", "claude-opus-4-5"
   *    Gemini    → "gemini-2.0-flash", "gemini-2.5-pro"
   */
  model: string;
  /** Optional override for the temperature. Defaults to 0.3. */
  temperature?: number;
  /** Optional override for the max-tokens cap. Defaults to 1500. */
  maxTokens?: number;
}

export type ChatRole = "user" | "assistant";

export interface ChatMessage {
  role: ChatRole;
  content: string;
  /** Wall-clock timestamp the message was created on the client side. */
  ts: number;
  /** Provider/model used to generate this assistant turn (assistant only). */
  via?: string;
}

export interface ChatRequest {
  /** Conversation so far, oldest first. Last message is the user's question. */
  messages: ChatMessage[];
  /** System prompt — typically the dashboard context summary. */
  system: string;
}

export interface ChatResponse {
  /** Assistant text. */
  content: string;
  /** Provider model that produced it. */
  model: string;
  /** Usage info if the provider returned it. */
  usage?: {
    inputTokens?: number;
    outputTokens?: number;
  };
}

export interface LlmProvider {
  id: LlmProviderId;
  /** Human-readable label for UI. */
  label: string;
  /** Default model to suggest in the settings UI. */
  defaultModel: string;
  /** A few model names the user can pick from in a dropdown without typing. */
  modelHints: string[];
  complete(req: ChatRequest, settings: LlmSettings): Promise<ChatResponse>;
}

export class LlmError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
    this.name = "LlmError";
  }
}
